import React, { useState } from "react";

import { useGetLifePlansQuery, useGetLongTermDisabilityPlansQuery, useGetShortTermDisabilityPlansQuery } from "EmployerApplication/api";
import costCalculationBuilder from "EmployerApplication/costCalculationBuilder";
import { cleanEmployeeProfile, dollarDisplay } from "EmployerApplication/CostEstimationUtils";
import { getAllSelectedPlans } from "EmployerApplication/Selectors";
import { useSelector } from "react-redux";

import {
  Table,
  HeaderTR,
  TH,
  TD,
  EllipsisTD,
  TR,
  NestedTR,
  SpacerTR,
  FooterTR,
  FooterTD,
  ToggleTR,
  DetailTR,
  CollapseRowsOrganizer,
  CollapseBody,
} from "Cobalt/TableV2";
import { getCart } from "Common/CartService";
import { SizeSwitch } from "Common/Responsive";

const MobilePriceDetailRows = ({ row }) => (
  <>
    <DetailTR>
      <TD>Payroll Deduction</TD>
      <TD align="right">{dollarDisplay(row.employeeCost)}</TD>
    </DetailTR>
    <DetailTR>
      <TD>Company Share</TD>
      <TD align="right">{dollarDisplay(row.companyCost)}</TD>
    </DetailTR>
  </>
);

const MobileDependentPriceDetailRows = ({ row }) => (
  <>
    <DetailTR nested>
      <TD>Payroll Deduction</TD>
      <TD align="right">{dollarDisplay(row.dependentsCompanyCost)}</TD>
    </DetailTR>
    <DetailTR nested>
      <TD>Company Share</TD>
      <TD align="right">{dollarDisplay(row.dependentsEmployeeCost)}</TD>
    </DetailTR>
  </>
);

const MobileRow = ({ row, lastRow }) => (
  <CollapseRowsOrganizer>
    {({ isOpen, toggle }) => (
      <>
        <tbody>
          <ToggleTR isOpen={isOpen} toggle={toggle} withBorder={!lastRow && (isOpen || !row.dependentsPremium)}>
            <EllipsisTD minWidth="130px">{row.name}</EllipsisTD>
            <TD align="right">{dollarDisplay(row.premium)}</TD>
          </ToggleTR>
        </tbody>
        <CollapseBody isOpen={isOpen}>
          <MobilePriceDetailRows row={row} />
          <SpacerTR />
        </CollapseBody>
        {row.dependentsPremium > 0 ? (
          <>
            <tbody>
              <NestedTR>
                <TD>Dependents</TD>
                <TD align="right">{dollarDisplay(row.dependentsPremium)}</TD>
                <TD />
              </NestedTR>
            </tbody>
            <CollapseBody isOpen={isOpen}>
              <MobileDependentPriceDetailRows row={row} />
            </CollapseBody>
            <tbody>
              <SpacerTR />
            </tbody>
          </>
        ) : null}
      </>
    )}
  </CollapseRowsOrganizer>
);

const EmployeeDesktopRow = ({ row }) => (
  <TR highlightFirstCell={false} withBorder={!row.dependentsPremium}>
    <TD>{row.name}</TD>
    <TD align="right">{dollarDisplay(row.premium)}</TD>
    <TD align="right">{dollarDisplay(row.companyCost)}</TD>
    <TD align="right">{dollarDisplay(row.employeeCost)}</TD>
  </TR>
);

const DependentDesktopRow = ({ row, name }) => (
  <NestedTR>
    <TD>{name}</TD>
    <TD align="right">{dollarDisplay(row.dependentsPremium)}</TD>
    <TD align="right">{dollarDisplay(row.dependentsCompanyCost)}</TD>
    <TD align="right">{dollarDisplay(row.dependentsEmployeeCost)}</TD>
  </NestedTR>
);

export const PayrollContributionTable = ({ contributionType, employeesCosts, employeeProfiles, contributions, showMultiplePlansDisclaimer }) => {
  if (cleanEmployeeProfile(employeeProfiles).length > 0 && employeesCosts.length > 0) {
    const { medicalPlans, dentalPlan, visionPlan } = useSelector(getAllSelectedPlans);

    const companyIDToken = useSelector((state) => state.companyProfile?.idToken);

    const cart = useSelector((state) => state.cart);
    const selectedLifePlanId = cart.life;
    const { data: lifePlanData } = useGetLifePlansQuery({ companyIDToken }, { skip: !selectedLifePlanId });

    const lifePlans = lifePlanData?.results;
    const lifePlan = (lifePlans || []).find((plan) => plan.id == selectedLifePlanId);

    const [fullTimeEmployees, _setFullTimeEmployees] = useState(employeeProfiles.map((employee) => employee.full_time));

    const { short_term_disability: shortTermPlanId, long_term_disability: longTermPlanId } = useSelector(getCart);

    const { data: availableShortTermPlans } = useGetShortTermDisabilityPlansQuery({ companyIDToken }, { skip: !shortTermPlanId });

    const { data: availableLongTermPlans } = useGetLongTermDisabilityPlansQuery({ companyIDToken }, { skip: !longTermPlanId });

    const shortTermPlan = availableShortTermPlans?.results?.find((plan) => plan.id === shortTermPlanId);

    const longTermPlan = availableLongTermPlans?.results?.find((plan) => plan.id === longTermPlanId);

    const shortTermEmployeeCosts = shortTermPlan?.employee_costs_include_part_time;
    const longTermEmployeeCosts = longTermPlan?.employee_costs_include_part_time;

    const rows = costCalculationBuilder({
      employeeProfiles,
      overwriteEmployeeProfilesEnrollingStatus: true,
      contributions,
      contributionType,
      medicalPlans,
      dentalPlan,
      visionPlan,
      lifePlan,
      fullTimeEmployees,
      shortTermEmployeeCosts,
      longTermEmployeeCosts,
    });

    return (
      <SizeSwitch
        xs={
          <Table>
            <thead>
              <HeaderTR>
                <TH>Employee Name</TH>
                <TH align="right" fitContent>
                  Monthly Premium{showMultiplePlansDisclaimer ? "*" : ""}
                </TH>
                <TH />
              </HeaderTR>
            </thead>
            {rows.employeesBreakdowns
              ? rows.employeesBreakdowns.map((row, index) => (
                  <MobileRow key={index} row={row} key={index} index={index} lastRow={index === rows.employeesBreakdowns.length - 1} />
                ))
              : null}
          </Table>
        }
        sm={
          <Table>
            <thead>
              <HeaderTR>
                <TH>Employee Name</TH>
                <TH align="right">Monthly Premium{showMultiplePlansDisclaimer ? "*" : ""}</TH>
                <TH align="right">Company Share</TH>
                <TH align="right">Payroll Deduction</TH>
              </HeaderTR>
            </thead>
            <tbody>
              {rows.employeesBreakdowns
                ? rows.employeesBreakdowns.map((employee, index) => (
                    <React.Fragment key={index}>
                      <EmployeeDesktopRow row={employee} index={index} />
                      {employee.dependentsPremium > 0 && <DependentDesktopRow row={employee} name={`${employee.name} Dependents`} index={index} />}
                    </React.Fragment>
                  ))
                : null}
            </tbody>
            <tfoot>
              <FooterTR>
                <FooterTD>Total</FooterTD>
                <FooterTD align="right">{`${dollarDisplay(rows.companyTotals.totalPremium)}`}</FooterTD>
                <FooterTD align="right">{`${dollarDisplay(rows.companyTotals.totalCompanyCost)}`}</FooterTD>
                <FooterTD align="right">{`${dollarDisplay(rows.companyTotals.totalEmployeeCost)}`}</FooterTD>
              </FooterTR>
            </tfoot>
          </Table>
        }
      />
    );
  }
  return null;
};

export default PayrollContributionTable;
